/**
 * paletteFilter.ts — PixiJS v8 Filter subclass for HSL palette-swap.
 *
 * # What this file owns (Layer 4b, Option B)
 *
 * A PixiJS v8 `Filter` subclass that wraps the GLSL + WGSL shaders from
 * `paletteShader.glsl.ts` and `paletteShader.wgsl.ts`, plus a cached
 * factory that returns a singleton filter instance per `PaletteId`.
 *
 * Three responsibilities:
 *
 *   1. **Filter construction.** Build a v8 `Filter` with both `glProgram`
 *      (WebGL) and `gpuProgram` (WebGPU) so the filter renders correctly
 *      under either backend. Filter omitting a program for the active
 *      backend is silently skipped — providing both is non-negotiable.
 *
 *   2. **Uniform management.** Pack the four HSL deltas from
 *      `PALETTE_TRANSFORMS` into a single `vec4<f32>` uniform exposed to
 *      both shaders as `uPaletteTransform`. Convert hueShift from degrees
 *      to radians at construction time so the shader doesn't pay the
 *      conversion cost per fragment.
 *
 *   3. **Cached singleton factory.** Expose
 *      `PaletteFilter.forPalette(paletteId)` returning a process-lifetime
 *      cached filter instance. Multiple sprites using the same palette
 *      share the same Filter (per Decision 3 — uniforms don't change
 *      per-sprite for the same palette, so sharing is correct and saves
 *      GPU resource creation).
 *
 *      Returns `null` for `paletteId === 'default'` to signal "skip
 *      filter assignment entirely" to the SpriteRenderer (Decision 5).
 *      Default-palette sprites pay zero shader cost.
 *
 * # Why both GlProgram and GpuProgram (LOCKED Layer 4b)
 *
 * Per `node_modules/.pnpm/pixi.js@8.18.1/.../ColorMatrixFilter.mjs`,
 * PixiJS v8 Filter constructor accepts both `glProgram` and `gpuProgram`,
 * and the active renderer picks the one matching its backend. Omitting
 * the program for the active backend is silently skipped — no error,
 * just visual breakage.
 *
 * For the foreseeable future, browsers are split between WebGL2 (mostly
 * Firefox / older Chrome / older Safari) and WebGPU (Chrome / Edge /
 * recent Safari with flag). Shipping both shaders is non-negotiable for
 * solo-dev shipping to "any browser the user happens to have."
 *
 * # Why cached singleton per PaletteId (LOCKED Decision 3)
 *
 * Within a single PaletteId, all sprites using that palette share
 * identical uniforms. There's no per-sprite variation. Caching one
 * Filter per PaletteId and sharing it across all sprites:
 *
 *   - Eliminates redundant Filter constructor cost (program compilation,
 *     uniform allocation) per-sprite.
 *
 *   - Keeps GPU memory low: one shader program per palette, not one
 *     per sprite using that palette.
 *
 *   - Matches PixiJS's own pattern (see how built-in Filters are usually
 *     instantiated once and applied to many sprites).
 *
 * The cache is process-lifetime; we never invalidate. PaletteId values
 * are static program data, so cache entries live until process exit.
 * If we ever add per-frame-animated palettes (e.g. shimmering effect),
 * we'll need a separate non-cached Filter pathway — out of scope for
 * Layer 4b.
 *
 * # Why null return for default (LOCKED Decision 5)
 *
 * The 'default' PaletteId is the no-op identity transform. Applying any
 * Filter has GPU cost — the sprite gets rendered to an off-screen
 * texture, the shader runs over every fragment, the result is composited
 * back. For 'default' palette, this is wasted work that produces a
 * pixel-identical result to skipping the filter entirely.
 *
 * Returning `null` from `forPalette('default')` lets the SpriteRenderer
 * unambiguously signal "no filter" by setting `sprite.filters = []`
 * (the established clean-state contract from SpritePool). Treating
 * 'default' as "no filter" rather than "identity filter" is structurally
 * cleaner and significantly faster on every default-palette sprite.
 *
 * # PixiJS v8 Filter constructor shape (LOCKED from PRE-FLIGHT 19)
 *
 *     new Filter({
 *       glProgram:  GlProgram.from({ vertex, fragment, name? }),
 *       gpuProgram: GpuProgram.from({
 *         vertex:   { source, entryPoint: 'mainVertex' },
 *         fragment: { source, entryPoint: 'mainFragment' },
 *       }),
 *       resources: {
 *         paletteUniforms: new UniformGroup({
 *           uPaletteTransform: { type: 'vec4<f32>', value: [...] },
 *         }),
 *       },
 *     });
 *
 * The `resources` key name (`paletteUniforms`) MUST match the WGSL
 * `@group(1) @binding(0)` binding name in `paletteShader.wgsl.ts`.
 * Mismatch causes WebGPU compile error or silent uniform-not-bound.
 *
 * @see paletteTransforms.ts — HSL deltas per PaletteId
 * @see paletteShader.glsl.ts — GLSL fragment + vertex shader source
 * @see paletteShader.wgsl.ts — WGSL combined source
 */

import { Filter, GlProgram, GpuProgram, UniformGroup } from 'pixi.js';
import type { PaletteId } from '@aevons-wake/core';

import {
  PALETTE_TRANSFORMS,
  degreesToRadians,
  type PaletteTransform,
} from './paletteTransforms';
import {
  paletteVertexShader,
  paletteFragmentShader,
} from './paletteShader.glsl';
import { paletteShaderWgsl } from './paletteShader.wgsl';

/**
 * Cache of PaletteId → constructed PaletteFilter singleton.
 *
 * Process-lifetime cache. Lazily populated on first call to
 * `PaletteFilter.forPalette(id)`. The 'default' palette is never cached
 * (forPalette returns null directly).
 *
 * Module-scope private state. External code accesses cache exclusively
 * through `PaletteFilter.forPalette()` — never directly.
 */
const filterCache: Map<PaletteId, PaletteFilter> = new Map();

/**
 * PaletteFilter — PixiJS v8 Filter that applies an HSL hue-rotation
 * transform to a sprite's pixels.
 *
 * Construct via the static factory `PaletteFilter.forPalette(paletteId)`,
 * not directly. The factory enforces caching and the null-for-default
 * convention.
 *
 * Direct construction is `internal` — exposed `protected` rather than
 * `private` so the test suite can construct fresh instances bypassing
 * the cache. Production code uses `forPalette()` exclusively.
 */
export class PaletteFilter extends Filter {
  /**
   * The PaletteId this filter was constructed for. Stored for
   * test-introspection and debug logging. Never mutated after construction.
   */
  public readonly paletteId: PaletteId;

  /**
   * The HSL transform values this filter applies. Stored for
   * test-introspection. Never mutated after construction.
   */
  public readonly transform: PaletteTransform;

  /**
   * Construct a PaletteFilter for a specific PaletteId.
   *
   * Test-only direct construction. Production code uses
   * `PaletteFilter.forPalette()`.
   *
   * Throws if called with `'default'` — default palette must skip filter
   * assignment entirely, not construct an identity filter. This guard
   * catches callers who bypass the factory.
   */
  protected constructor(paletteId: PaletteId, transform: PaletteTransform) {
    if (paletteId === 'default') {
      throw new Error(
        `[PaletteFilter] Cannot construct filter for 'default' palette. ` +
          `Default palette skips filter assignment entirely; use ` +
          `PaletteFilter.forPalette('default') which returns null.`,
      );
    }

    // Pack the four HSL deltas into a single vec4 uniform.
    // Note: hueShift is converted to radians here so the shader doesn't
    // pay the trivial conversion cost per fragment.
    const paletteUniforms = new UniformGroup({
      uPaletteTransform: {
        type: 'vec4<f32>',
        value: new Float32Array([
          degreesToRadians(transform.hueShift),
          transform.satScale,
          transform.lightShift,
          transform.contrast,
        ]),
      },
    });

    const glProgram = GlProgram.from({
      vertex: paletteVertexShader,
      fragment: paletteFragmentShader,
      name: `palette-filter-${paletteId}`,
    });

    const gpuProgram = GpuProgram.from({
      vertex: {
        source: paletteShaderWgsl,
        entryPoint: 'mainVertex',
      },
      fragment: {
        source: paletteShaderWgsl,
        entryPoint: 'mainFragment',
      },
    });

    super({
      glProgram,
      gpuProgram,
      resources: {
        paletteUniforms,
      },
    });

    this.paletteId = paletteId;
    this.transform = transform;
  }

  /**
   * Get the cached PaletteFilter for a PaletteId, constructing on first
   * access.
   *
   * Returns `null` for `paletteId === 'default'` to signal that the
   * SpriteRenderer should skip filter assignment entirely (Decision 5).
   *
   * For all other palettes, returns the same filter instance across
   * calls — sprites sharing the same palette share the same Filter
   * (Decision 3). The cache is process-lifetime.
   *
   * @param paletteId The PaletteId to get a filter for.
   * @returns The cached PaletteFilter, or null for 'default'.
   *
   * @example
   *   const filter = PaletteFilter.forPalette('crimson');
   *   if (filter !== null) {
   *     sprite.filters = [filter];
   *   } else {
   *     sprite.filters = [];  // 'default' palette
   *   }
   */
  public static forPalette(paletteId: PaletteId): PaletteFilter | null {
    if (paletteId === 'default') {
      return null;
    }

    const cached = filterCache.get(paletteId);
    if (cached !== undefined) {
      return cached;
    }

    const transform = PALETTE_TRANSFORMS[paletteId];
    const filter = new PaletteFilter(paletteId, transform);
    filterCache.set(paletteId, filter);
    return filter;
  }

  /**
   * Clear the filter cache.
   *
   * Test-only utility. Allows test suites to build fresh filter
   * instances per `beforeEach` without process-lifetime cache pollution
   * across tests.
   *
   * Production code should never call this — the cache is a correctness
   * mechanism (singleton sharing per Decision 3), not just an
   * optimization.
   */
  public static clearCache(): void {
    filterCache.clear();
  }

  /**
   * Get the current cache size. Test-only utility for verifying cache
   * behavior.
   */
  public static cacheSize(): number {
    return filterCache.size;
  }
}
